import React, {
  createRef,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMediaQuery } from "react-responsive";
import useInterval from "use-interval";
import { useInView } from "react-intersection-observer";
import classNames from "classnames";
import { useThrottle } from "@uidotdev/usehooks";
import useWindowScroll from "../../hooks/useWindowScroll";
import NextSlideButton from "../SectionNavigation/NextSlideButton";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import sleepUntil from "../../utils/sleepUntil";
import { OverlayContext } from "../../functional-components/OverlayContext";
import "./style.scss";

const INIT_TIMEOUT = 10000;
const SMOOTH_SCROLL_TIMEOUT = 1200;

type CarouselProps = {
  slidesData: object[];
  renderSlide: (data: object, index: number) => ReactNode;
  renderThumb?: (data: object, index: number) => ReactNode;
  renderTab?: (data: object, index: number) => ReactNode;
  renderThumbPlaceholder?: () => ReactNode;
  renderTabPlaceholder?: () => ReactNode;
  navType?: "thumbs" | "dots" | "tabs" | "arrows";
  direction?: "horizontal" | "vertical";
  loop?: boolean; //seamless looping
  startOverAtEnd?: boolean; //looping by scrolling back to first slide
  showMarkers?: boolean;
  smallMarkers?: boolean;
  hideButtons?: boolean;
  centeredButtons?: boolean;
  autoplayInterval?: number;
  startIndex?: number;
  onSlideChange?: (slideIndex: number) => void;
  offset?: number;
  className?: string;
};

const Carousel = forwardRef(function Carousel(props: CarouselProps, ref) {
  ref = ref || createRef();

  const {
    slidesData = [],
    renderSlide,
    renderThumb,
    renderTab,
    renderThumbPlaceholder,
    renderTabPlaceholder,
    navType = "thumbs",
    direction = "horizontal",
    loop = false,
    startOverAtEnd = false,
    showMarkers = false,
    smallMarkers = false,
    hideButtons = false,
    centeredButtons = false,
    autoplayInterval = 0,
    startIndex = 0,
    onSlideChange,
    offset = 0,
    className
  } = props;

  type OverlayContextType = {
    overlayOpened: boolean;
    setOverlayOpened: (v: boolean) => void;
    flyoutOpened: boolean;
    registerFlyoutOpened: () => void;
    registerFlyoutClosed: () => void;
  };

  const { overlayOpened, flyoutOpened } = useContext(
    OverlayContext
  ) as unknown as OverlayContextType;
  const isTouchDevice = useMediaQuery({ query: "(pointer: coarse)" });
  const isSmall = useMediaQuery({ maxWidth: 599.99 });
  const container = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const tabContainer = useRef<HTMLDivElement>(null);
  const scrollPos = useWindowScroll(container.current, direction);
  const throttledScrollPos = useThrottle(scrollPos, 100);
  const presentSlides = useRef<boolean[]>([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [lastVisibleIndex, setLastVisibleIndex] = useState(0);
  const indexQueueOffset = useRef(0);
  const [paused, setPaused] = useState(false);
  const [allSlidesLoaded, setAllSlidesLoaded] = useState<boolean>(false);
  const [inViewRef, inView] = useInView();

  const scrollAttr = direction === "vertical" ? "top" : "left";
  const offsetAttr = direction === "vertical" ? "offsetTop" : "offsetLeft";
  //const scrollValueAttr = direction === "vertical" ? "scrollTop" : "scrollLeft";
  const sizeAttr = direction === "vertical" ? "clientHeight" : "clientWidth";

  const containerWidth: number = container.current?.[sizeAttr] || 0;

  const scrollTabIntoView = useCallback(
    (index: number) => {
      if (navType === "tabs" && isSmall) {
        const tab = tabContainer.current?.children[index];
        tab?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest"
        });
      }
    },
    [isSmall, navType]
  );

  const gotoSlide = useCallback(
    (
      index: number,
      triggerCallback: boolean = false,
      nonAnimated: boolean = false
    ) => {
      //console.log("Carousel::gotoSlide", index);
      const slideElms = _getSlideElms();
      const actualSlideCount = slideElms.length - (loop ? 2 : 0);
      if (
        index >= (loop ? -1 : 0) &&
        index < actualSlideCount + (loop ? 1 : 0)
      ) {
        const slidePositions: number[] = slideElms
          .map(slide => slide[offsetAttr])
          .filter((pos, index) => pos > 0 || index === 0);

        const realIndex = (index + actualSlideCount) % actualSlideCount;
        const lookupIndex = loop ? index + 1 : index;
        const scrollToPos = slidePositions[lookupIndex] - offset;
        container.current?.scrollTo({
          [scrollAttr]: scrollToPos,
          behavior: nonAnimated ? "instant" : "smooth"
        });
        setTimeout(
          () => {
            scrollTabIntoView(realIndex);
          },
          nonAnimated ? 1 : 1000
        );

        if (triggerCallback) {
          window.setTimeout(
            //TODO: Use scrollend event instead
            () => {
              indexQueueOffset.current = 0;
              onSlideChange?.(realIndex);
            },
            nonAnimated ? 0 : SMOOTH_SCROLL_TIMEOUT
          );
        }
      }
    },
    [loop, offset, offsetAttr, onSlideChange, scrollAttr, scrollTabIntoView]
  );

  const gotoNext = useCallback(
    (reverse: boolean = false) => {
      const slideElms = _getSlideElms();
      const actualSlideCount = slideElms.length - (loop ? 2 : 0);

      indexQueueOffset.current += reverse ? -1 : 1;
      const nextIndex = reverse
        ? Math.max(activeIndex + indexQueueOffset.current, loop ? -1 : 0)
        : Math.min(
            activeIndex + indexQueueOffset.current,
            actualSlideCount - (loop ? 0 : 1)
          );
      gotoSlide(nextIndex, true);
    },
    [activeIndex, gotoSlide, loop]
  );

  const slides = useMemo(
    () =>
      React.Children.map(slidesData.map(renderSlide), (element, i) => {
        return React.cloneElement(element as ReactElement, {
          "data-slideid": "slide-" + i
        });
      }),
    [renderSlide, slidesData]
  );

  const thumbs = useMemo(
    () =>
      renderThumb && allSlidesLoaded
        ? React.Children.toArray(
            slidesData
              .filter((slide, i) => presentSlides.current[i])
              .map((slide, index) =>
                renderThumb(
                  {
                    ...slide,
                    isActive: activeIndex === index,
                    onClick: () => {
                      setPaused(true);
                      gotoSlide(index);
                    }
                  },
                  index
                )
              )
          )
        : renderThumb && renderThumbPlaceholder
        ? renderThumbPlaceholder()
        : [],
    [
      activeIndex,
      allSlidesLoaded,
      gotoSlide,
      renderThumb,
      renderThumbPlaceholder,
      slidesData
    ]
  );

  const tabs = useMemo(
    () =>
      renderTab && allSlidesLoaded
        ? React.Children.toArray(
            slidesData
              .filter((slide, i) => presentSlides.current[i])
              .map((slide, index) =>
                renderTab(
                  {
                    ...slide,
                    isActive: activeIndex === index,
                    onClick: () => {
                      setPaused(true);
                      gotoSlide(index);
                    }
                  },
                  index
                )
              )
          )
        : renderTab && renderTabPlaceholder
        ? renderTabPlaceholder()
        : [],
    [
      activeIndex,
      allSlidesLoaded,
      gotoSlide,
      renderTab,
      renderTabPlaceholder,
      slidesData
    ]
  );

  useInterval(
    () => {
      if (!paused && inView && !overlayOpened && !flyoutOpened) {
        gotoNext();
      }
    },
    autoplayInterval > 0 ? autoplayInterval * 1000 : false
  );

  const _getSlideElms = () => {
    return track.current
      ? Array.from(track.current.childNodes.values()).map(n => n as HTMLElement)
      : [];
  };

  const _allSlidesLoaded = useCallback(() => {
    if (allSlidesLoaded) return true;

    if (
      !(track.current !== null && (track.current?.childNodes?.length || 0 > 0))
    ) {
      return false;
    }

    const slideElms = _getSlideElms();
    const loaded =
      slideElms.findIndex(slide => slide.childElementCount === 0) === -1 &&
      slideElms.findIndex(slide => slide?.[sizeAttr] === 0) === -1 &&
      container.current?.getElementsByClassName("loading").length === 0;

    if (loaded) {
      presentSlides.current = slidesData.map(
        (data, i) =>
          slideElms.findIndex(
            elm => elm.getAttribute("data-slideid") === "slide-" + i
          ) > -1
      );
    }

    console.log("Carousel::_allSlidesLoaded:", loaded);
    return loaded;
  }, [allSlidesLoaded, sizeAttr, slidesData]);

  useLayoutEffect(() => {
    const waitForSlidesToLoad = async () => {
      await sleepUntil(_allSlidesLoaded, INIT_TIMEOUT);
      if (startIndex !== 0) gotoSlide(startIndex, false, true);
      window.setTimeout(() => {
        setAllSlidesLoaded(true);
      }, 1000);
    };
    waitForSlidesToLoad();
  }, [_allSlidesLoaded, gotoSlide, startIndex]);

  useLayoutEffect(() => {
    /** When loop is true, the first slide is a copy of the last one, so move to the next */
    const firstSlideWidth: number = _getSlideElms()?.[0][sizeAttr] || 0;
    if (loop && slides && slides.length > 1) {
      container.current?.scrollTo({
        [scrollAttr]: firstSlideWidth - offset,
        behavior: "instant"
      });
    }
  }, [loop, offset, scrollAttr, sizeAttr, slides]);

  const getNearestSlidePos = useCallback(
    (targetPos: number) => {
      if (allSlidesLoaded) {
        const slideElms = _getSlideElms();
        const actualSlideCount = slideElms.length - (loop ? 2 : 0);
        const slidePositions: number[] = slideElms.map(
          slide => slide[offsetAttr]
        );
        let nearestIndex = slidePositions.findIndex((pos, index) => {
          const halfWidth =
            (index < slideElms.length - 1
              ? slidePositions[index + 1] - slidePositions[index]
              : slidePositions[1] - slidePositions[0]) / 2;
          return pos + halfWidth > targetPos;
        });
        const slidePositionsRev = slidePositions.slice().reverse();
        const lastVisibleIndexRev = slidePositionsRev.findIndex(
          (pos, index) => {
            const halfWidth =
              (index > 1
                ? slidePositionsRev[index - 1] - slidePositionsRev[index]
                : slidePositionsRev[0] - slidePositionsRev[1]) / 2;
            return pos + halfWidth < targetPos + containerWidth;
          }
        );
        const lastVisibleIndex = slideElms.length - 1 - lastVisibleIndexRev;
        const actualNearestPos = slideElms[nearestIndex]?.[offsetAttr] - offset;
        let needsPosChange = false;
        if (nearestIndex === -1) {
          nearestIndex = slidePositions.length - 1;
        }
        if (loop && nearestIndex >= actualSlideCount + 1) {
          nearestIndex = 1;
          needsPosChange = true;
        }
        if (loop && nearestIndex === 0) {
          nearestIndex = actualSlideCount;
          needsPosChange = true;
        }
        const nearestPos = slideElms[nearestIndex]?.[offsetAttr] - offset;
        const realIndex =
          (nearestIndex - (loop ? 1 : 0) + actualSlideCount) % actualSlideCount;
        const realLastVisibleIndex =
          (lastVisibleIndex - (loop ? 1 : 0) + actualSlideCount) %
          actualSlideCount;

        /*console.log(
          "Carousel::getNearestSlidePos",
          targetPos,
          nearestPos,
          nearestIndex,
          realIndex,
          realLastVisibleIndex
        );*/

        return {
          nearestPos,
          realIndex,
          realLastVisibleIndex,
          needsPosChange,
          actualNearestPos
        };
      } else {
        return {
          nearestPos: undefined,
          realIndex: undefined,
          realLastVisibleIndex: undefined,
          needsPosChange: undefined,
          actualNearestPos: undefined
        };
      }
    },
    [allSlidesLoaded, loop, offsetAttr, offset, containerWidth]
  );

  /** Triggers on scroll position change */
  useLayoutEffect(() => {
    if (allSlidesLoaded) {
      const {
        nearestPos,
        realIndex,
        realLastVisibleIndex,
        needsPosChange,
        actualNearestPos
      } = getNearestSlidePos(throttledScrollPos);

      if (needsPosChange && actualNearestPos === throttledScrollPos) {
        container.current?.scrollTo({
          [scrollAttr]: nearestPos,
          behavior: "instant"
        });
      }
      if (
        realLastVisibleIndex !== undefined &&
        lastVisibleIndex !== realLastVisibleIndex
      ) {
        setLastVisibleIndex(realLastVisibleIndex);
      }
      if (realIndex !== undefined && activeIndex !== realIndex) {
        setActiveIndex(realIndex);
        scrollTabIntoView(realIndex);
        onSlideChange?.(realIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [throttledScrollPos, offsetAttr, loop, allSlidesLoaded, offset, scrollAttr, onSlideChange, getNearestSlidePos, scrollTabIntoView]); //(activeIndex will cause a loop)

  const slideElms = _getSlideElms();
  const actualSlideCount = slideElms.length - (loop ? 2 : 0);

  const nextPage = useCallback(
    (reverse: boolean = false) => {
      indexQueueOffset.current += reverse ? -1 : 1;
      const startOver =
        !reverse && startOverAtEnd && lastVisibleIndex >= actualSlideCount - 1;

      const targetPos = startOver
        ? 0
        : throttledScrollPos + indexQueueOffset.current * containerWidth * 0.95;
      const { nearestPos } = getNearestSlidePos(targetPos);

      if (nearestPos !== undefined) {
        container.current?.scrollTo({
          [scrollAttr]: nearestPos,
          behavior: "smooth"
        });

        window.setTimeout(
          //TODO: Use scrollend event instead
          () => {
            indexQueueOffset.current = 0;
          },
          SMOOTH_SCROLL_TIMEOUT / 2
        );
      }
    },
    [
      startOverAtEnd,
      lastVisibleIndex,
      actualSlideCount,
      throttledScrollPos,
      containerWidth,
      getNearestSlidePos,
      scrollAttr
    ]
  );

  //To call methods from outside:
  useImperativeHandle(ref, () => ({
    gotoNext,
    gotoSlide,
    gotoIndex: gotoSlide,
    nextPage
  }));

  return (
    <>
      <div
        className={classNames(
          "carousel",
          "carousel-navtype-" + navType,
          className
        )}
        ref={inViewRef}
      >
        {navType === "tabs" && tabs && (
          <div className={classNames("carousel-tabs")}>
            <div
              className={classNames("carousel-tabs-inner")}
              ref={tabContainer}
            >
              {tabs}
            </div>
          </div>
        )}
        <div ref={container} className="carousel-container">
          <div
            ref={track}
            className={classNames(
              "carousel-slides",
              "carousel-slides-" + direction
            )}
            onTouchMove={() => setPaused(true)}
            onWheel={e => {
              if (
                (direction === "horizontal" &&
                  e.deltaY == 0 &&
                  e.deltaX !== 0) ||
                (direction === "vertical" && e.deltaX == 0 && e.deltaY !== 0)
              ) {
                setPaused(true);
              }
            }}
          >
            {loop &&
              slides &&
              slides.length > 1 &&
              React.cloneElement(
                allSlidesLoaded ? slides[slides.length - 1] : slides[0],
                {
                  key: "loop-slide-back"
                }
              )}
            {slides}
            {loop &&
              slides &&
              slides.length > 1 &&
              allSlidesLoaded &&
              React.cloneElement(slides[0], {
                key: "loop-slide-forward"
              })}
          </div>
        </div>
        {!isTouchDevice &&
          direction === "horizontal" &&
          !isSmall &&
          !hideButtons &&
          allSlidesLoaded &&
          slides &&
          slides.length > 1 /* back */ && (
            <NextSlideButton
              onClick={() => {
                nextPage(true);
              }}
              slideDirection={direction}
              direction={-1}
              returnIcon={activeIndex === 0}
              centered={centeredButtons}
              disabled={!loop && activeIndex <= 0}
            />
          )}
        {!isTouchDevice &&
          !hideButtons &&
          allSlidesLoaded &&
          slides &&
          slides.length > 1 /* forward */ && (
            <NextSlideButton
              onClick={() => {
                nextPage(false);
              }}
              slideDirection={direction}
              direction={1}
              centered={centeredButtons}
              returnIcon={
                startOverAtEnd && lastVisibleIndex >= actualSlideCount - 1
              }
              disabled={
                !loop && !startOverAtEnd && activeIndex >= actualSlideCount - 1
              } /* TODO: does not work for non-full-width slides */
            />
          )}

        {navType === "thumbs" && !isSmall && thumbs ? (
          <div className={classNames("carousel-thumbs")}>{thumbs}</div>
        ) : (
          (navType === "dots" ||
            ((navType === "arrows" ||
              navType === "thumbs" ||
              navType === "tabs") &&
              showMarkers)) &&
          direction === "horizontal" &&
          allSlidesLoaded &&
          slides &&
          slides.length > 1 && (
            <div
              className={classNames("carousel-horizontal-markers", {
                "small-markers": smallMarkers,
                "bold-markers": navType === "tabs",
                dots: navType === "dots"
              })}
            >
              <div className="markers-container">
                {slides.map((s, i) => (
                  <div
                    key={i}
                    className={classNames("marker", {
                      active: i >= activeIndex && i <= lastVisibleIndex
                    })}
                    onClick={() => {
                      setPaused(true);
                      gotoSlide(i, true);
                    }}
                  ></div>
                ))}
              </div>
            </div>
          )
        )}
      </div>
      <KeyboardEventHandler
        handleKeys={["left", "right"]}
        handleFocusableElements
        isDisabled={!!slides && slides.length <= 1}
        onKeyEvent={key => {
          switch (key) {
            case "left":
              gotoNext(true);
              break;
            case "right":
              gotoNext(false);
              break;
            default:
          }
        }}
      />
    </>
  );
});

export default Carousel;
